export interface UserCohort {
  id: number;
  name: string;
}

/** Reported per recipient in the send response; the backend resolves it. */
export type UserAuthMethod = 'password' | 'sso';

/** `pending` covers provisioned users who have never been emailed. */
export type WelcomeEmailStatus = 'pending' | 'sent' | 'failed';

export type ActivationStatus =
  | 'not_invited'
  | 'invited'
  | 'activated'
  | 'unknown';

export interface User {
  id: number;
  name?: string;
  email: string;
  role?: 'admin' | 'user';
  active?: boolean;
  first_name: string;
  last_name: string;
  username: string;
  cohorts?: UserCohort[];
  variables?: Record<string, unknown>;
  activation_status?: ActivationStatus | string;
  welcome_email_status?: WelcomeEmailStatus;
  welcome_email_sent_at?: string | null;
}

export function isUserVariableFilled(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'boolean') {
    return true;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  if (Array.isArray(value)) {
    return value.some((item) => isUserVariableFilled(item));
  }

  return false;
}

export interface UserVariableStatus {
  name: string;
  filled: boolean;
}

export function getUserVariableStatuses(
  variables?: Record<string, unknown>
): UserVariableStatus[] {
  if (!variables) return [];

  return Object.keys(variables)
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({
      name,
      filled: isUserVariableFilled(variables[name]),
    }));
}

export function getUserVariableFillCounts(
  variables?: Record<string, unknown>
): { filled: number; total: number } {
  const statuses = getUserVariableStatuses(variables);

  return {
    filled: statuses.filter((status) => status.filled).length,
    total: statuses.length,
  };
}

export function formatUserVariableFillSummary(
  variables?: Record<string, unknown>
): string {
  const { filled, total } = getUserVariableFillCounts(variables);

  if (total === 0) {
    return '—';
  }

  return `${filled}/${total} filled`;
}

export function getUserCohortIds(cohorts?: UserCohort[]): number[] {
  return cohorts?.map((cohort) => cohort.id) ?? [];
}

export function userHasNoCohorts(cohorts?: UserCohort[]): boolean {
  return getUserCohortIds(cohorts).length === 0;
}

export function formatUserCohortNames(cohorts?: UserCohort[]): string {
  const names = cohorts?.map((cohort) => cohort.name).filter(Boolean) ?? [];
  return names.length > 0 ? names.join(', ') : '—';
}

export interface UpdateUserCohortsPayload {
  cohorts: number[];
}

export interface BulkCohortAssignmentItem {
  user_id: number;
  cohorts: number[];
}

export type BulkCohortAssignmentPayload = BulkCohortAssignmentItem[];

export function getWelcomeEmailStatus(user: User): WelcomeEmailStatus {
  return user.welcome_email_status ?? 'pending';
}

export function resolveActivationStatus(value: unknown): ActivationStatus {
  if (
    value === 'not_invited' ||
    value === 'invited' ||
    value === 'activated' ||
    value === 'unknown'
  ) {
    return value;
  }

  return 'unknown';
}

export function getActivationStatus(user: Pick<User, 'activation_status'>): ActivationStatus {
  return resolveActivationStatus(user.activation_status);
}

export function formatActivationStatus(status: ActivationStatus): string {
  switch (status) {
    case 'not_invited':
      return 'Not Invited';
    case 'invited':
      return 'Invited';
    case 'activated':
      return 'Activated';
    default:
      return 'Unknown';
  }
}

export function hasBeenInvited(user: User): boolean {
  const status = getActivationStatus(user);
  return status === 'invited' || status === 'activated';
}

export function formatWelcomeEmailSentAt(sentAt?: string | null): string {
  if (!sentAt) return '';

  const date = new Date(sentAt);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export interface SendWelcomeEmailPayload {
  user_ids: number[];
  isSSO: boolean;
}

export interface SendWelcomeEmailResult {
  user_id: number;
  email: string;
  status: 'sent' | 'failed';
  auth_method: UserAuthMethod;
  detail?: string;
}

export interface SendWelcomeEmailResponse {
  sent_count: number;
  failed_count: number;
  results: SendWelcomeEmailResult[];
}

export interface TenantUser {
  tenant_id: number;
  is_owner: boolean;
  user: User;
  activation_status?: ActivationStatus | string;
}

export type CreateUserData = Omit<User, 'id'>;
export type UpdateUserData = Partial<CreateUserData>;

export interface UserPermission {
  id: number;
  codename: string;
}

export interface HardDeleteTenantSummary {
  id: number;
  name: string;
  slug: string;
}

export interface PendingHardDeleteRequest {
  request_id: string;
  task_id: string;
  status: string;
}

export interface HardDeleteImpactResponse {
  user_id: number;
  email: string;
  username: string;
  current_tenant_id: number;
  active_tenant_count: number;
  active_tenants: HardDeleteTenantSummary[];
  target_active_tenant_ids: number[];
  has_other_active_tenants: boolean;
  pending_hard_delete_request: PendingHardDeleteRequest | null;
}

export interface HardDeleteUserPayload {
  confirmation: 'HARD DELETE';
  reason: string;
  acknowledged_other_tenants: boolean;
}

export interface HardDeleteUserResponse {
  request_id: string;
  task_id: string;
  status: string;
}
